import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';

/**
 * Angular's HttpErrorResponse implements Error but does not extend Error,
 * so Sentry.captureException treats it as a plain object and emits
 * "Object captured as exception with keys: error, headers, message, name, ok".
 */
export function toSentryHttpError(error: HttpErrorResponse): Error {
  const status = error.status ?? 'unknown';
  const message =
    error.message ||
    `HTTP ${status}${error.url ? ` for ${error.url}` : ''}`;

  const sentryError = new Error(message);
  sentryError.name = 'HttpErrorResponse';

  return sentryError;
}

export function shouldReportHttpErrorToSentry(
  error: HttpErrorResponse,
): boolean {
  // Network failure or unexpected server error — skip expected 4xx client errors
  return error.status === 0 || error.status >= 500;
}

export function httpErrorSentryExtra(
  error: HttpErrorResponse,
  request?: { url?: string; method?: string; body?: unknown },
): Record<string, unknown> {
  return {
    url: request?.url ?? error.url,
    method: request?.method,
    status: error.status,
    statusText: error.statusText,
    response: error.error,
    payload: request?.body,
  };
}

export class FormSubmitError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'FormSubmitError';
    this.status = status;
  }
}

interface FormControlLike {
  valid: boolean;
  status: string;
  errors: Record<string, unknown> | null;
  value: unknown;
}

interface FormLike {
  valid?: boolean;
  status?: string;
  controls?: Record<string, FormControlLike>;
}

/** Field validity only — values stay out of Sentry (name, email, phone, message). */
export function formFieldsSentryExtra(
  form: FormLike | null | undefined,
): Record<string, unknown> {
  if (!form?.controls) {
    return { formValid: null, formStatus: null, fields: null };
  }

  const fields: Record<string, unknown> = {};
  for (const name of Object.keys(form.controls)) {
    const control = form.controls[name];
    const value = control.value;
    const filled =
      typeof value === 'boolean'
        ? value
        : value !== null &&
          value !== undefined &&
          String(value).trim() !== '';

    fields[name] = {
      valid: control.valid,
      status: control.status,
      errors: control.errors ? Object.keys(control.errors) : [],
      filled,
    };
  }

  return {
    formValid: form.valid ?? null,
    formStatus: form.status ?? null,
    fields,
  };
}

/**
 * Report a contact/cooperation submit failure.
 * `status` is set on the Error (0 when the request never left the browser).
 */
export function captureFormSubmitException(
  error: unknown,
  extra: Record<string, unknown>,
): string {
  const httpError = error instanceof HttpErrorResponse ? error : null;
  const status =
    typeof extra['status'] === 'number'
      ? extra['status']
      : httpError
        ? httpError.status
        : 0;
  const message = httpError
    ? httpError.message || `HTTP ${status}`
    : error instanceof Error
      ? error.message
      : 'Form submit failed';
  const sentryError = new FormSubmitError(
    `${String(extra['form'] ?? 'form')} submit failed (status ${status}): ${message}`,
    status,
  );

  if (error instanceof Error && error.stack) {
    sentryError.stack = error.stack;
  }

  return Sentry.captureException(sentryError, {
    tags: {
      http_status: String(status),
      form: String(extra['form'] ?? 'unknown'),
      phase: String(extra['phase'] ?? 'submit'),
      site: String(extra['site'] ?? 'unknown'),
    },
    extra: {
      status,
      statusText: httpError?.statusText ?? null,
      url: httpError?.url ?? null,
      response: httpError?.error ?? null,
      errorName: error instanceof Error ? error.name : null,
      errorMessage: message,
      ...extra,
    },
  });
}

/** Capture an exception, converting HttpErrorResponse into a real Error for Sentry. */
export function captureSentryException(
  error: unknown,
  captureContext?: Parameters<typeof Sentry.captureException>[1],
): string {
  if (error instanceof HttpErrorResponse) {
    const context =
      captureContext && typeof captureContext === 'object'
        ? (captureContext as Record<string, unknown>)
        : {};

    return Sentry.captureException(toSentryHttpError(error), {
      ...context,
      tags: {
        http_status: String(error.status),
        ...((context['tags'] as Record<string, string>) ?? {}),
      },
      extra: {
        ...httpErrorSentryExtra(error),
        ...((context['extra'] as Record<string, unknown>) ?? {}),
      },
    });
  }

  return Sentry.captureException(error, captureContext);
}
